import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getFirestore, doc, getDoc, setDoc } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

const TablePredictions = () => {
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deadline, setDeadline] = useState(null);
  const [isDeadlinePassed, setIsDeadlinePassed] = useState(false);
  const [message, setMessage] = useState(null);
  const [hasPredictions, setHasPredictions] = useState(false);
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(false);
  const [seasonValue, setSeasonValue] = useState(null);
  const [userId, setUserId] = useState(null);

  useEffect(() => {
    const fetchTeamsAndDeadline = async () => {
      try {
        const [currentSeasonResponse, teamsResponse, deadlineResponse] = await Promise.all([
          axios.get(`${process.env.REACT_APP_SERVER_API_URL}/api/current-season`),
          axios.get(`${process.env.REACT_APP_SERVER_API_URL}/api/teams`),
          axios.get(`${process.env.REACT_APP_SERVER_API_URL}/api/table-predictions/deadline`)
        ]);

        const currentSeason = currentSeasonResponse.data.season;
        setSeasonValue(currentSeason);

        const processedTeams = teamsResponse.data.map((team, index) => ({
          ...team,
          position: index + 1,
          inputPosition: index + 1
        }));

        const auth = getAuth();
        const user = auth.currentUser;
        setUserId(user?.uid);

        if (user) {
          const db = getFirestore();
          const userDocRef = doc(db, 'table_predictions', `${user.uid}_${currentSeason}`);
          const userDocSnap = await getDoc(userDocRef);

          if (userDocSnap.exists()) {
            const userPredictionsTemp = userDocSnap.data();

            // Keep only key pairs where key begins with 'position_'
            const userPredictions = Object.fromEntries(
              Object.entries(userPredictionsTemp).filter(([key]) => key.startsWith('position_'))
            );

            const predictedTeams = Object.keys(userPredictions)
              .sort()
              .map(key => userPredictions[key])
              .map((name, index) => {
                const team = processedTeams.find(t => t.name === name);
                return { ...team, position: index + 1, inputPosition: index + 1 };
              });
            setTeams(predictedTeams);
            setHasPredictions(true);
          } else {
            setTeams(processedTeams);
          }
        } else {
          setTeams(processedTeams);
        }

        const deadlineDate = new Date(deadlineResponse.data.deadline);
        setDeadline(deadlineDate);
        setIsDeadlinePassed(new Date() > deadlineDate);

        setLoading(false);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('The Fantasy Premier League API is updating, please come back soon.');
        setLoading(false);
      }
    };
    fetchTeamsAndDeadline();
  }, []);

  useEffect(() => {
    if (deadline) {
      const timer = setInterval(() => {
        setIsDeadlinePassed(new Date() > deadline);
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [deadline]);

  useEffect(() => {
    const checkSubmitDisabled = async () => {
      if (!hasPredictions) {
        setIsSubmitDisabled(false);
        return;
      }

      const auth = getAuth();
      const user = auth.currentUser;
      if (!user) {
        setIsSubmitDisabled(false);
        return;
      }

      const db = getFirestore();
      const userDocRef = doc(db, 'table_predictions', `${user.uid}_${seasonValue}`);
      const userDocSnap = await getDoc(userDocRef);

      if (userDocSnap.exists()) {
        const userPredictions = userDocSnap.data();
        const predictedTeams = Object.keys(userPredictions)
          .sort()
          .map(key => userPredictions[key]);

        const currentTeams = teams.map(team => team.name);

        setIsSubmitDisabled(JSON.stringify(predictedTeams) === JSON.stringify(currentTeams));
      } else {
        setIsSubmitDisabled(false);
      }
    };

    checkSubmitDisabled();
  }, [teams, hasPredictions, seasonValue]);

  const moveTeam = (index, direction) => {
    if (isDeadlinePassed) return;
    if ((direction === 'up' && index === 0) || (direction === 'down' && index === teams.length - 1)) {
      return;
    }

    const newTeams = [...teams];
    const swapIndex = direction === 'up' ? index - 1 : index + 1;
    [newTeams[index], newTeams[swapIndex]] = [newTeams[swapIndex], newTeams[index]];

    const updatedTeams = newTeams.map((team, idx) => ({ ...team, position: idx + 1, inputPosition: idx + 1 }));
    setTeams(updatedTeams);
  };

  const handleInputChange = (index, value) => {
    if (isDeadlinePassed) return;
    const newTeams = [...teams];
    newTeams[index].inputPosition = value;
    setTeams(newTeams);
  };

  const moveTeamToPosition = (index) => {
    if (isDeadlinePassed) return;
    const team = teams[index];
    const newPosition = parseInt(team.inputPosition, 10);

    if (isNaN(newPosition) || newPosition < 1 || newPosition > teams.length) {
      setMessage({ text: 'Please enter a valid position number.', type: 'error' });
      return;
    }

    const newTeams = teams.filter((_, idx) => idx !== index);
    newTeams.splice(newPosition - 1, 0, team);

    const updatedTeams = newTeams.map((team, idx) => ({ ...team, position: idx + 1, inputPosition: idx + 1 }));
    setTeams(updatedTeams);
    setMessage(null);
  };

  const submitPredictions = async () => {
    if (isDeadlinePassed) {
      setMessage({ text: 'The deadline has passed. You can no longer submit predictions.', type: 'error' });
      return;
    }

    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      setMessage({ text: 'You must be logged in to submit predictions.', type: 'error' });
      return;
    }

    const db = getFirestore();
    const predictionsData = {};
    teams.forEach((team, index) => {
      const positionKey = `position_${(index + 1).toString().padStart(2, '0')}`;
      predictionsData[positionKey] = team.name;
    });
    predictionsData['user_id'] = user.uid;

    try {
      await setDoc(doc(db, 'table_predictions', `${user.uid}_${seasonValue}`), predictionsData);
      const timestamp = new Date().toLocaleString();
      setMessage({ text: `Your predictions have been submitted successfully - (${timestamp})`, type: 'success' });
      setHasPredictions(true);
    } catch (error) {
      console.error('Error submitting predictions:', error);
      setMessage({ text: 'Failed to submit predictions. Please try again.', type: 'error' });
    }
  };

  if (loading) return (
    <div className="flex justify-center items-center h-screen">
      <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-premier-purple"></div>
    </div>
  );

  if (error) {
    return (
      <div className="flex flex-col justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-red-500 mb-4"></div>
        <div className="text-center text-red-500 text-xl font-bold">{error}</div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
      className="container mx-auto px-4 py-8 max-w-3xl"
    >
      <h1 className="text-3xl font-bold mb-6 text-premier-purple text-center">Premier League Table Predictor</h1>
      <p className="mt-2 text-gray-700 text-center mb-6">Visit the <Link to="/scoring-guide?openRulesOn=table" className="text-premier-purple font-bold underline hover:text-premier-pink">Scoring Guide</Link> for scoring details.</p>
  
      {deadline && (
        <div className={`mb-4 p-3 rounded-lg text-center text-white text-sm ${isDeadlinePassed ? 'bg-red-500' : 'bg-green-500'}`}>
          {isDeadlinePassed ? (
            <>
              <span>Prediction window closed.</span>
              <a href={`/performance?uid=${userId}&openOnTab=tablePredictor`} className="block mt-1 underline font-bold">View Live Score</a>
            </>
          ) : (
            `Deadline: ${deadline.toLocaleString()}`
          )}
        </div>
      )}
  
      {message && (
        <div className={`mb-4 p-3 rounded-lg text-white text-center text-sm ${message.type === 'error' ? 'bg-red-500' : 'bg-green-500'}`}>
          {message.text}
        </div>
      )}
  
      {!isDeadlinePassed && (
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={submitPredictions}
          className="w-full mb-4 bg-premier-purple text-white py-3 px-4 font-bold text-lg rounded-lg shadow-md hover:bg-premier-pink transition duration-300"
          disabled={isSubmitDisabled}
        >
          Submit Prediction
        </motion.button>
      )}
  
      <ul className="space-y-2">
        {teams.map((team, index) => (
          <motion.li
            key={team.id}
            layout
            transition={{ duration: 0.3 }}
            className="flex items-center bg-white p-3 rounded-lg shadow-sm"
          >
            <span className="mr-3 font-bold text-lg text-premier-purple w-8 text-center">{team.position}</span>
            <div className="w-8 h-8 mr-3 bg-gray-400 rounded-full flex items-center justify-center overflow-hidden">
              <img src={team.logo} alt={team.name} className="w-6 h-6 object-contain" />
            </div>
            <span className="text-gray-800 font-medium flex-grow truncate">{team.name}</span>
            <div className="flex items-center">
              <input
                type="number"
                min="1"
                max={teams.length}
                value={team.inputPosition}
                onChange={(e) => handleInputChange(index, e.target.value)}
                className="w-12 p-1 mr-2 border rounded text-center"
                disabled={isDeadlinePassed}
              />
              <button
                onClick={() => moveTeamToPosition(index)}
                className="bg-premier-purple text-white px-2 py-1 rounded text-sm hover:bg-premier-pink"
                disabled={isDeadlinePassed}
              >
                Move
              </button>
              <button
                onClick={() => moveTeam(index, 'up')}
                className="text-xl text-premier-purple hover:text-premier-pink focus:outline-none ml-2"
                disabled={index === 0 || isDeadlinePassed}
              >
                ▲
              </button>
              <button
                onClick={() => moveTeam(index, 'down')}
                className="text-xl text-premier-purple hover:text-premier-pink focus:outline-none ml-1"
                disabled={index === teams.length - 1 || isDeadlinePassed}
              >
                ▼
              </button>
            </div>
          </motion.li>
        ))}
      </ul>
      <p className="text-gray-700 text-center mt-4">Don't forget to click the "Submit Prediction" button at the top of the page.</p>
    </motion.div>
  );
};

export default TablePredictions;